import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@trotroways/ui";
import { Crosshair, Layers, MinusIcon, PlusIcon } from "lucide-react";
import type { MapStyleName } from "@/hooks/use-map";
import { cn } from "@trotroways/utils";

export interface MapControlsProps {
  onRecenter: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  mapStyle: MapStyleName;
  onMapStyleChange: (style: MapStyleName) => void;
  locationDenied: boolean;
}

const MAP_STYLE_OPTIONS: Array<{
  value: MapStyleName;
  label: string;
  preview: string;
}> = [
    {
      value: "standard",
      label: "Standard",
      preview: "/map-style-standard.svg",
    },
    {
      value: "satellite",
      label: "Satellite",
      preview: "/map-style-satellite.svg",
    },
  ];

export const MapControls = memo(function MapControls({
  onRecenter,
  onZoomIn,
  onZoomOut,
  mapStyle,
  onMapStyleChange,
  locationDenied,
}: MapControlsProps) {
  const [isStylePanelOpen, setIsStylePanelOpen] = useState(false);
  const stylePanelRef = useRef<HTMLDivElement>(null);

  const activeStyle = useMemo(
    () => MAP_STYLE_OPTIONS.find((style) => style.value === mapStyle),
    [mapStyle],
  );

  useEffect(() => {
    if (!isStylePanelOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!stylePanelRef.current) return;
      const target = event.target as Node;
      if (!stylePanelRef.current.contains(target)) {
        setIsStylePanelOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isStylePanelOpen]);

  return (
    <>
      <div className="absolute right-2 top-4 z-20 md:right-5" ref={stylePanelRef}>
        <div className="flex flex-col gap-1 rounded-xl bg-zinc-900/75 py-1 px-0.5 shadow-lg shadow-black/40 backdrop-blur-xl border border-white/10">
          <Popover>
            <PopoverTrigger render={
              <Button
                size="icon"
                className="rounded-xl text-white"
                variant="ghost"
                onClick={() => setIsStylePanelOpen((open) => !open)}
                title={`Map style: ${activeStyle?.label ?? "Style"}`}
              >
                <Layers />
              </Button>
            } />
            <PopoverContent side="left" sideOffset={12} alignOffset={34} className="w-48 rounded-xl border bg-zinc-950/80 shadow-xl shadow-black/45 backdrop-blur-xl">
              <div className="flex gap-1">
                {MAP_STYLE_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    onClick={() => {
                      onMapStyleChange(option.value);
                      setIsStylePanelOpen(false);
                    }}
                    className="flex flex-col w-full items-center gap-1 rounded-xl p-1 text-left transition-colors"
                  >
                    <div className={cn("h-12 w-16 overflow-hidden rounded-lg border", option.value === mapStyle ? "border-cyan-300/80" : "border-white/20")}>
                      <img src={option.preview} alt={`${option.label} map style preview`} className="h-full w-full object-cover" />
                    </div>
                    <p className="text-xs font-medium text-zinc-100">{option.label}</p>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <Button
            size="icon"

            variant="ghost"
            className={cn("rounded-xl text-white", locationDenied && "border-amber-300/35 text-amber-200")}
            onClick={onRecenter}
            title="Re-center to your location"
          >
            <Crosshair />
          </Button>
        </div>
      </div>

      <div className="hidden md:block absolute bottom-72 right-2 z-20 md:bottom-4 md:right-5">
        <div className="flex flex-col gap-1 rounded-xl bg-zinc-900/75 py-1 px-0.5 shadow-lg shadow-black/40 backdrop-blur-xl border border-white/10">
          <Button
            size="icon"
            className="rounded-xl text-white"
            variant="ghost"
            onClick={onZoomIn}
          >
            <PlusIcon />
          </Button>

          <Button
            size="icon"

            variant="ghost"
            className="rounded-xl text-white"
            onClick={onZoomOut}
          >
            <MinusIcon />
          </Button>
        </div>
      </div>
    </>
  );
});
